/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { SessionErrorResponseCodeEnum } from 'client-code/session-manager';
import { LiveTvErrorBase } from './LiveTvErrorBase';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { ErrorTypes } from '../../error';
import { Log } from '../../logging';
import { VisualDisplayTypes } from '../enums';
import { LIVE_TV_SESSION_ERROR_PRIORITY } from '../../../config';

export class LiveTvSessionError extends LiveTvErrorBase {
    TAG: string = 'LiveTvSessionError';

    public processErrorByCode(error: any) {
        Log.i(this.TAG, 'process session Error', error);
        switch (error.getCode()) {
            case SessionErrorResponseCodeEnum.AuthenticationExpired:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_MESSAGE);
                this.uiErrorCode =
                    ErrorTypes.LIVE_TV_SESSION_ERROR_AUTHENTICATION_EXPIRED_ERROR_V409;
                break;

            case SessionErrorResponseCodeEnum.BadArgument:
            case SessionErrorResponseCodeEnum.BadConfiguration:
            case SessionErrorResponseCodeEnum.Deprecated:
            case SessionErrorResponseCodeEnum.ExternalError:
            case SessionErrorResponseCodeEnum.HostUnavailable:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137;
                break;

            case SessionErrorResponseCodeEnum.InternalError:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_INTERNAL_ERROR_V138;
                break;

            case SessionErrorResponseCodeEnum.NoSession:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_NO_SESSION_MESSAGE);
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_NO_SESSION_V922;
                break;

            case SessionErrorResponseCodeEnum.NotAuthenticated:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_SOMETHING_WENT_WRONG_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_SOMETHING_WENT_WRONG_MESSAGE,
                );
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHENTICATED_V403;
                break;

            case SessionErrorResponseCodeEnum.NotAuthorized:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_SUBSCRIPTION_REQUIRED_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_SUBSCRIPTION_REQUIRED_MESSAGE,
                );
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_V410;
                break;

            case SessionErrorResponseCodeEnum.NotAuthorizedDeviceType:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_DEVICE_TYPE_V414;
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_CANNOT_PLAY_ON_THIS_DEVICE_OSD_MESSAGE,
                );
                this.uiVisualType = VisualDisplayTypes.OSD;
                break;

            case SessionErrorResponseCodeEnum.NotAuthorizedOutOfHome:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.uiErrorCode = ErrorTypes.LIVE_TV_OOH_OSD_ERROR_V404;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_OOH_OSD_MESSAGE);
                this.uiVisualType = VisualDisplayTypes.OSD;
                break;

            case SessionErrorResponseCodeEnum.NotAuthorizedOutOfRegion:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.uiErrorCode = ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_OUT_OF_REGION_OSD_MESSAGE);
                this.uiVisualType = VisualDisplayTypes.OSD;
                break;

            case SessionErrorResponseCodeEnum.NotRegistered:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_SHOWS_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_SHOWS_MESSAGE);
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_REGISTERED_V406;
                break;

            case SessionErrorResponseCodeEnum.TooManyStreams:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_NOW_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_NOW_MESSAGE);
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_TOO_MANY_STREAMS_V407;
                break;

            case SessionErrorResponseCodeEnum.TooManyStreamsForResource:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_NOW_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_TOO_MANY_STREAMS_FOR_RESOURCE_MESSAGE,
                );
                this.uiErrorCode =
                    ErrorTypes.LIVE_TV_SESSION_ERROR_TOO_MANY_STREAMS_FOR_RESOURCE_ERROR_V408;
                break;

            case SessionErrorResponseCodeEnum.UnknownLocation:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.uiErrorCode = ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_OUT_OF_REGION_OSD_MESSAGE);
                this.uiVisualType = VisualDisplayTypes.OSD;
                break;

            default:
                this.priority = LIVE_TV_SESSION_ERROR_PRIORITY;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE);
                this.uiErrorCode = ErrorTypes.LIVE_TV_SESSION_ERROR_UNKNOWN_ERROR_V603;
                break;
        }
    }
}
